import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import {
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  IUser,
} from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IComparisonPopulationSetting } from '../comparisonPopulationSettingsProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface IComparisonReportSettings {
  testType: ISelectableTestType;
  metrics: {
    [testTypeId: string]: ISelectableMetric[];
  };
  population: {
    tags: ISelectableTag[];
  };
  target: {
    tags: ISelectableTag[];
  };
}
/**
 * @description the complete document in the database
 */
export interface IComparisonReportSettingEntity
  extends IComparisonReportSettingOptions {
  selected: ISettingsTemplate<IComparisonReportSettings>;
}

interface IComparisonReportSettingOptions {
  targetTimeframe: IDateRange;
  comparison: IComparisonPopulationSetting;
}

export interface IComparisonReportSettingsState
  extends IComparisonReportSettingOptions {
  selected: ISettingsTemplate<IComparisonReportSettings>;
  templates: Array<ISettingsTemplate<IComparisonReportSettings>>;
}

/**
 * @description observes the comparison report settings
 */
export const comparisonReportSettingsProvider =
  (): Observable<IComparisonReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap(
        (
          user: IUser
        ): Observable<
          [
            ISettingsTemplate<IComparisonReportSettings>,
            IComparisonReportSettingOptions,
            Array<ISettingsTemplate<IComparisonReportSettings>>
          ]
        > => {
          const listenerArgs = {
            userId: user.id,
            firestore: user.services.firestore,
          };
          return combineLatest([
            selectedTemplateProvider(listenerArgs),
            comparisonSettingsOptionsProvider(listenerArgs),
            templatesProvider(listenerArgs),
          ]);
        }
      ),
      map(
        (
          result: [
            ISettingsTemplate<IComparisonReportSettings>,
            IComparisonReportSettingOptions,
            Array<ISettingsTemplate<IComparisonReportSettings>>
          ]
        ): IComparisonReportSettingsState => {
          return {
            selected: result[0],
            templates: result[2],
            ...result[1],
          };
        }
      )
    );
  };

interface IListenerArgs {
  userId: string;
  firestore: Firestore;
}
/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: IListenerArgs
): Observable<Array<ISettingsTemplate<IComparisonReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/comparisonReport/templates`
      ) as CollectionReference<ISettingsTemplate<IComparisonReportSettings>>,
      where('active', '==', true)
    )
  );
};

/**
 * @description observes changes the subset of the comparison settings that are not template specific
 * @param userId
 * @returns
 */
const comparisonSettingsOptionsProvider = (
  args: IListenerArgs
): Observable<IComparisonReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData<IComparisonReportSettingEntity>(
    doc(
      firestore,
      `userProfiles/${userId}/settings/comparisonReport`
    ) as DocumentReference<IComparisonReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IComparisonReportSettingEntity
      ): IComparisonReportSettingOptions => ({
        targetTimeframe: entity?.targetTimeframe,
        comparison: entity?.comparison,
      })
    ),
    distinctUntilChanged(
      (
        prev: IComparisonReportSettingOptions,
        curr: IComparisonReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: IListenerArgs
): Observable<ISettingsTemplate<IComparisonReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/comparisonReport`
    ) as DocumentReference<IComparisonReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IComparisonReportSettingEntity
      ): ISettingsTemplate<IComparisonReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IComparisonReportSettings>,
        curr: ISettingsTemplate<IComparisonReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};
